import {
  isAuthenticationError,
  isCancelledRequest,
  wrapApiError,
} from "../apiErrors";
import { createApiBatchKey, createResourceApiKey } from "../cache/apiKey";
import type { ApiKey } from "../cache/types";
import { client } from "../client";
import type { ApiResourceRoute } from "../resources/types";
import { useApiStore } from "../store";
import useSWR, { type SWRResponse } from "swr";
import {
  getRequestConfig,
  getSWRConfig,
  mergeApiQueryConfig,
} from "./queryConfig";
import type { ApiBatchResult, ApiGetConfig, ApiQueryConfig } from "./types";
import { ApiBatchError } from "./types";

function isBatchResourceRoute<TEntity>(
  route:
    | null
    | undefined
    | readonly ApiResourceRoute<TEntity>[]
    | ApiResourceRoute<TEntity>,
): route is readonly ApiResourceRoute<TEntity>[] {
  return Array.isArray(route);
}

export function useApiGet<TEntity, TResponse = TEntity>(
  routes: readonly ApiResourceRoute<TEntity>[],
  config: ApiQueryConfig & { withError: true },
): SWRResponse<ApiBatchResult<TResponse>[]>;

export function useApiGet<TEntity, TResponse = TEntity>(
  routes: readonly ApiResourceRoute<TEntity>[],
  config?: ApiQueryConfig & { withError?: false },
): SWRResponse<TResponse[]>;

export function useApiGet<TEntity, TResponse = TEntity>(
  resourceRoute: ApiResourceRoute<TEntity> | null | undefined,
  config?: ApiQueryConfig,
): SWRResponse<TResponse>;

export function useApiGet<TEntity>(
  resourceRoute:
    | null
    | undefined
    | readonly ApiResourceRoute<TEntity>[]
    | ApiResourceRoute<TEntity>,
  config?: ApiGetConfig,
): SWRResponse<TEntity | TEntity[] | ApiBatchResult<TEntity>[]> {
  const isBatch = isBatchResourceRoute(resourceRoute);
  const { withError = false, ...queryConfig } = config ?? {};
  const { disableCache } = mergeApiQueryConfig(queryConfig);
  useApiStore();

  const key = isBatch
    ? createApiBatchKey(resourceRoute, withError ? "withError" : "data")
    : createResourceApiKey(resourceRoute, "get");

  return useSWR(
    key,
    async ({ route: keyedRoute, requestedRoute, batchMode }: ApiKey) => {
      if (typeof requestedRoute === "string") {
        try {
          const response = await client().get<TEntity>(
            requestedRoute,
            getRequestConfig(disableCache),
          );
          return response.data;
        } catch (error) {
          throw wrapApiError(error);
        }
      }

      const captureErrors = batchMode === "withError";
      return Promise.all(
        requestedRoute.map(async (requestRoute, index) => {
          try {
            const response = await client().get<TEntity>(
              requestRoute,
              getRequestConfig(disableCache),
            );
            if (!captureErrors) return response.data;
            return { result: response.data, error: undefined };
          } catch (error) {
            if (isCancelledRequest(error)) throw error;
            const apiError = wrapApiError(error);
            if (isAuthenticationError(apiError) || !captureErrors)
              throw apiError;
            return {
              result: undefined,
              error: new ApiBatchError(
                Array.isArray(keyedRoute) ? keyedRoute[index] : requestRoute,
                apiError,
              ),
            };
          }
        }),
      );
    },
    getSWRConfig(config),
  ) as SWRResponse<TEntity | TEntity[] | ApiBatchResult<TEntity>[]>;
}
